import React, { useState, useEffect } from 'react';
import haukeApiService from '../../services/haukeApi';
import '../../assets/css/profil-admin.css';

const Oeffnungszeiten = ({ hofId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [notification, setNotification] = useState(null);
  const [openingHoursForm, setOpeningHoursForm] = useState({
    selectedDays: [],
    openTime: '10:00',
    closeTime: '20:00'
  });

  const dayLabels = {
    monday: 'Montag',
    tuesday: 'Dienstag', 
    wednesday: 'Mittwoch',
    thursday: 'Donnerstag',
    friday: 'Freitag',
    saturday: 'Samstag',
    sunday: 'Sonntag'
  };

  useEffect(() => {
    loadData();
  }, [hofId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await haukeApiService.getDirekterzeugerDetails(hofId);
      
      // Konvertiere die Daten zur Objekt-Struktur wie in Profile.js
      let openingHours = {
        monday: { open: '', close: '', isClosed: true },
        tuesday: { open: '', close: '', isClosed: true },
        wednesday: { open: '', close: '', isClosed: true },
        thursday: { open: '', close: '', isClosed: true },
        friday: { open: '', close: '', isClosed: true },
        saturday: { open: '', close: '', isClosed: true },
        sunday: { open: '', close: '', isClosed: true }
      };

      // Wenn Array-Struktur vorhanden, konvertiere zu Objekt
      if (response.data.openingHours && Array.isArray(response.data.openingHours)) {
        response.data.openingHours.forEach(hour => {
          if (hour.day && openingHours[hour.day]) {
            openingHours[hour.day] = {
              open: hour.open || '',
              close: hour.close || '',
              isClosed: hour.isClosed !== false
            };
          }
        });
      }

      setData({ ...response.data, openingHours });
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleOpeningHoursChange = (day, field, value) => {
    setData(prevData => ({
      ...prevData,
      openingHours: {
        ...prevData.openingHours,
        [day]: {
          ...prevData.openingHours[day],
          [field]: value
        }
      }
    }));
    setHasChanges(true);
  };

  // Gruppierte Öffnungszeiten-Logik aus Profile.js
  const getGroupedOpeningHours = () => {
    const groups = [];
    const processed = new Set();
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    days.forEach(day => {
      if (processed.has(day)) return;
      
      const dayHours = data.openingHours[day];
      
      // Prüfe ob der Tag geöffnet ist und Zeiten hat
      if (!dayHours || dayHours.isClosed === true || !dayHours.open || !dayHours.close) {
        return;
      }
      
      const sameTimeDays = days.filter(d => {
        const dHours = data.openingHours[d];
        return !processed.has(d) && 
               dHours && 
               dHours.isClosed !== true && 
               dHours.open === dayHours.open && 
               dHours.close === dayHours.close &&
               dHours.open && dHours.close;
      });
      
      sameTimeDays.forEach(d => processed.add(d));
      
      groups.push({
        days: sameTimeDays.map(d => dayLabels[d]),
        dayKeys: sameTimeDays,
        openTime: dayHours.open,
        closeTime: dayHours.close
      });
    });
    
    return groups;
  };

  const handleDayToggle = (day) => {
    setOpeningHoursForm(prev => ({
      ...prev,
      selectedDays: prev.selectedDays.includes(day)
        ? prev.selectedDays.filter(d => d !== day)
        : [...prev.selectedDays, day]
    }));
  };

  const handleSaveOpeningHours = () => {
    const { selectedDays, openTime, closeTime } = openingHoursForm;
    
    if (selectedDays.length === 0) {
      alert('Bitte wählen Sie mindestens einen Tag aus.');
      return;
    }
    
    if (!openTime || !closeTime) {
      alert('Bitte geben Sie Öffnungs- und Schließzeiten ein.');
      return;
    }
    
    // Mapping von deutschen zu englischen Tagen
    const dayMapping = {
      'Montag': 'monday',
      'Dienstag': 'tuesday',
      'Mittwoch': 'wednesday',
      'Donnerstag': 'thursday',
      'Freitag': 'friday',
      'Samstag': 'saturday',
      'Sonntag': 'sunday'
    };
    
    selectedDays.forEach(germanDay => {
      const englishDay = dayMapping[germanDay];
      if (englishDay) {
        handleOpeningHoursChange(englishDay, 'isClosed', false);
        handleOpeningHoursChange(englishDay, 'open', openTime);
        handleOpeningHoursChange(englishDay, 'close', closeTime);
      }
    });
    
    setOpeningHoursForm({
      selectedDays: [],
      openTime: '10:00',
      closeTime: '20:00'
    });
    
    setNotification('Öffnungszeiten hinzugefügt. Vergessen Sie nicht, die Änderungen zu speichern.');
    setTimeout(() => setNotification(null), 3000);
  };

  const handleEditHoursGroup = (group) => {
    const dayMapping = {
      'monday': 'Montag',
      'tuesday': 'Dienstag',
      'wednesday': 'Mittwoch',
      'thursday': 'Donnerstag',
      'friday': 'Freitag',
      'saturday': 'Samstag',
      'sunday': 'Sonntag'
    };
    
    const germanDays = group.dayKeys.map(englishDay => dayMapping[englishDay]).filter(Boolean);
    
    setOpeningHoursForm({
      selectedDays: germanDays,
      openTime: group.openTime,
      closeTime: group.closeTime
    });
  };

  const handleDeleteHoursGroup = (group) => {
    group.dayKeys.forEach(day => {
      handleOpeningHoursChange(day, 'isClosed', true);
      handleOpeningHoursChange(day, 'open', '');
      handleOpeningHoursChange(day, 'close', '');
    });
  };

  const showSuccessToast = (message) => {
    const toast = document.createElement('div');
    toast.className = 'success-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      document.body.removeChild(toast);
    }, 3000);
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    try {
      setSaving(true);
      
      // Konvertiere die Objekt-Struktur zurück zu Array für die API
      const openingHoursArray = Object.entries(data.openingHours).map(([day, hours]) => ({
        id: day,
        day: day,
        open: hours.open || '',
        close: hours.close || '',
        isClosed: hours.isClosed !== false
      }));

      await haukeApiService.updateDirekterzeuger(hofId, { openingHours: openingHoursArray });
      setHasChanges(false);
      showSuccessToast('Öffnungszeiten erfolgreich gespeichert!');
    } catch (err) {
      console.error('Error saving:', err);
      alert('Fehler beim Speichern: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('Änderungen verwerfen?')) {
        loadData();
        setHasChanges(false);
      }
    }
  };

  if (loading) return <div className="webling-loading">Daten werden geladen...</div>;
  if (error) return <div className="webling-error">{error}</div>;
  if (!data) return <div className="webling-error">Keine Daten verfügbar</div>;

  return (
    <div className="webling-container">
      <div className="webling-content">
        <div className="opening-hours-grid">
        <div className="form-section">
          <h3>Aktuelle Öffnungszeiten</h3>
          <div className="opening-hours-section">
            <div className="hours-display">
              {getGroupedOpeningHours().length > 0 ? (
                <div className="hours-list">
                  {getGroupedOpeningHours().map((group, index) => (
                    <div key={index} className="hours-group">
                      <div className="hours-info">
                        <strong>{group.days.join(', ')}</strong>
                        <span className="hours-time">
                          {group.openTime} - {group.closeTime}
                        </span>
                      </div>
                      <div className="hours-actions">
                        <button 
                          className="btn-edit-hours"
                          onClick={() => handleEditHoursGroup(group)}
                          title="Bearbeiten"
                          disabled={saving}
                        >
                          <i className="material-icons">edit</i>
                        </button>
                        <button 
                          className="btn-delete-hours"
                          onClick={() => handleDeleteHoursGroup(group)}
                          title="Löschen"
                          disabled={saving}
                        >
                          <i className="material-icons">delete</i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <i className="material-icons">schedule</i>
                  <h3>Keine Öffnungszeiten definiert</h3>
                  <p>Für diesen Direkterzeuger sind noch keine Öffnungszeiten hinterlegt.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Öffnungszeiten hinzufügen</h3>
          <div className="add-hours-form">
            <div className="hours-form-content">
              <div className="days-selection">
                <label>Tage auswählen:</label>
                <div className="days-grid">
                  {['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'].map(day => (
                    <div 
                      key={day} 
                      className={`day-selector ${openingHoursForm.selectedDays.includes(day) ? 'selected' : ''}`}
                      onClick={() => handleDayToggle(day)}
                    >
                      <input 
                        type="checkbox" 
                        checked={openingHoursForm.selectedDays.includes(day)}
                        onChange={() => {}}
                        disabled={saving}
                      />
                      <span>{day}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="time-inputs">
                <div className="time-group">
                  <label>Von:</label>
                  <input
                    type="time"
                    value={openingHoursForm.openTime}
                    onChange={(e) => setOpeningHoursForm({...openingHoursForm, openTime: e.target.value})}
                    disabled={saving}
                  />
                </div>
                <div className="time-group">
                  <label>Bis:</label>
                  <input
                    type="time"
                    value={openingHoursForm.closeTime}
                    onChange={(e) => setOpeningHoursForm({...openingHoursForm, closeTime: e.target.value})}
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-add-hours"
                  onClick={handleSaveOpeningHours}
                  disabled={openingHoursForm.selectedDays.length === 0 || saving}
                >
                  Öffnungszeiten hinzufügen
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>

      </div>

      <div className="webling-footer">
        <button 
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="save-button"
        >
          {saving ? 'Speichern...' : 'Speichern'}
        </button>
        <button 
          onClick={handleCancel}
          disabled={saving}
          className="cancel-button"
        >
          Schließen
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div className="success-toast">
          {notification}
        </div>
      )}
    </div>
  );
};

export default Oeffnungszeiten; 